import { createFileRoute } from "@tanstack/react-router";
import { seoHead, absoluteImageUrl } from "@/lib/seo";
import jerry from "@/assets/jerry-hedges.jpg.asset.json";

// Route through `absoluteImageUrl` so the share preview URL is absolute,
// cache-busted with the asset's ID, and consistent with every other route
// — never hand-concatenate SITE_ORIGIN + asset.url.
const ABOUT_OG_IMAGE = absoluteImageUrl(jerry);

export const Route = createFileRoute("/about")({
  head: () =>
    seoHead({
      path: "/about",
      title: "About — Coffman Cove Lodging",
      description:
        "More than 40 years ago, Jerry and Caroline Hedges built the bunkhouse. Today, Travis and Heather Hedges carry on the tradition in Coffman Cove, Alaska.",
      image: ABOUT_OG_IMAGE,
    }),
  component: About,
});

function About() {
  return (
    <div>
      <section className="bg-[var(--color-deep)] text-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 pt-12 pb-10 sm:pt-20 sm:pb-16">
          <p className="uppercase tracking-[0.3em] text-xs text-white/80">About</p>
          <h1 className="font-display text-3xl sm:text-5xl mt-3 text-balance text-white">
            About Coffman Cove Lodging
          </h1>
        </div>
      </section>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 pt-10 pb-16 sm:py-20 grid md:grid-cols-2 gap-8 md:gap-14 items-start">

        <div>
        <div className="space-y-5 text-base sm:text-lg text-muted-foreground max-w-prose leading-relaxed">

          <p>
            More than 40 years ago, Jerry and Caroline Hedges built what was simply known as
            the bunkhouse—a place where friends could gather to experience everything they
            loved about Alaska: incredible fishing, breathtaking scenery, and the kind of
            hospitality that turns strangers into lifelong friends.
          </p>
          <p>What began as a simple place to stay has grown into a tradition spanning generations.</p>
          <p>
            Today, Coffman Cove Lodging is proudly owned and operated by Jerry's son,{" "}
            <span className="text-foreground font-medium">Travis Hedges</span>, and his wife,{" "}
            <span className="text-foreground font-medium">Heather</span>. Together, they
            continue the legacy Jerry and Caroline started, welcoming every guest with the
            same warmth, local knowledge, and genuine passion for Alaska that made the
            bunkhouse so special all those years ago.
          </p>
          <p>
            Many of our guests return year after year. Some have been making the trip for
            more than four decades, becoming part of our extended family and creating
            memories that span generations. Around here, you're not just booking a fishing
            trip—you become part of a tradition built on friendship, adventure, and
            unforgettable days on the water.
          </p>
          <p>
            Whether you're chasing trophy King Salmon, hauling in giant Halibut, or simply
            taking in the beauty of Southeast Alaska, our goal remains exactly as Jerry
            intended all those years ago.
          </p>
          <p>Because, as Jerry always said...</p>
        </div>

        <p className="mt-8 font-display italic text-2xl sm:text-3xl text-[var(--color-ember)]">
          "It's all about the fun."
        </p>
      </div>

      <figure className="md:sticky md:top-24">
        <img
          src={jerry.url}
          alt="Jerry Hedges in the lodge kitchen, 2002"
          loading="lazy"
          className="rounded-xl w-full object-cover shadow-lg ring-1 ring-black/10"
        />
        <figcaption className="mt-3 text-sm text-muted-foreground italic">
          Jerry Hedges — "Not a pretty sight, but a five star cook." — Thanks, Deno &amp; Crew, 7/02
        </figcaption>
      </figure>
      </div>
    </div>
  );
}

